import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { initGA, trackPageview } from "@/lib/analytics";
import Layout from "@/components/Layout";
import Marketing from "@/pages/Marketing";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";

// Lazy-loaded routes keep the initial bundle small so the marketing landing
// page paints fast; the authenticated app only loads once a user signs in.
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const MyWork = lazy(() => import("@/pages/MyWork"));
const Field = lazy(() => import("@/pages/Field"));
const ConfirmPage = lazy(() => import("@/pages/ConfirmPage"));
const CommandCentre = lazy(() => import("@/pages/CommandCentre"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const Leads = lazy(() => import("@/pages/Leads"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Purchasing = lazy(() => import("@/pages/Purchasing"));
const Receiving = lazy(() => import("@/pages/Receiving"));
const Equipment = lazy(() => import("@/pages/Equipment"));
const Fleet = lazy(() => import("@/pages/Fleet"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const PriceBook = lazy(() => import("@/pages/PriceBook"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const PayInvoice = lazy(() => import("@/pages/PayInvoice"));
const ApproveQuote = lazy(() => import("@/pages/ApproveQuote"));
const ApproveChangeOrder = lazy(() => import("@/pages/ApproveChangeOrder"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/Projects").then((m) => ({ default: m.ProjectDetail })));
const People = lazy(() => import("@/pages/People"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const Reports = lazy(() => import("@/pages/Reports"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const PlatformAdmin = lazy(() => import("@/pages/PlatformAdmin"));
const PlatformOrgConfig = lazy(() => import("@/pages/PlatformOrgConfig"));
const Setup = lazy(() => import("@/pages/Setup"));
const Portal = lazy(() => import("@/pages/Portal"));
const Support = lazy(() => import("@/pages/Support"));
const IndustryPage = lazy(() => import("@/pages/IndustryPage"));
const IndustryCityPage = lazy(() => import("@/pages/IndustryPage").then((m) => ({ default: m.IndustryCityPage })));
const Documents = lazy(() => import("@/pages/Documents"));
const VehicleLookup = lazy(() => import("@/pages/VehicleLookup"));
const Assessment = lazy(() => import("@/pages/Assessment"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Features = lazy(() => import("@/pages/Features"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const SetupServices = lazy(() => import("@/pages/SetupServices"));
const Resources = lazy(() => import("@/pages/Resources"));
const IndustriesIndex = lazy(() => import("@/pages/IndustriesIndex"));
const Faq = lazy(() => import("@/pages/Faq"));
const Trust = lazy(() => import("@/pages/Trust"));
const Legal = lazy(() => import("@/pages/Legal"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const Agreements = lazy(() => import("@/pages/Agreements"));
const Safety = lazy(() => import("@/pages/Safety"));

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="h-10 w-10 rounded-lg bg-amber-500 animate-pulse" />
    </div>
  );
}

function RouteAnalytics() {
  const loc = useLocation();
  useEffect(() => { initGA(); }, []);
  useEffect(() => { trackPageview(loc.pathname + loc.search); }, [loc.pathname, loc.search]);
  return null;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RootGate() {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user?.permissions?.platform) return <Navigate to="/platform" replace />;
  if (user) return <Navigate to="/my-work" replace />;
  return <Marketing />;
}

const INDUSTRY_SLUGS = ["roofing", "hvac", "plumbing", "electrical", "restoration",
  "landscaping", "cleaning", "construction", "painting", "property-maintenance",
  "mechanic", "fleet-management", "tire-shop"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteAnalytics />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<RootGate />} />
              <Route path="/assessment" element={<Assessment />} />
              <Route path="/features" element={<Features />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/setup-services" element={<SetupServices />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/:slug" element={<Resources />} />
              <Route path="/industries" element={<IndustriesIndex />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/privacy" element={<Legal key="privacy" />} />
              <Route path="/terms" element={<Legal key="terms" />} />
              <Route path="/support-legal" element={<Legal key="support-legal" />} />
              <Route path="/learn/:slug" element={<ArticlePage />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              {INDUSTRY_SLUGS.map((s) => (
                <Route key={s} path={`/${s}`} element={<IndustryPage industry={s} />} />
              ))}
              {INDUSTRY_SLUGS.map((s) => (
                <Route key={`${s}-city`} path={`/${s}/:city`} element={<IndustryCityPage industry={s} />} />
              ))}
              <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/reset-password" element={<GuestOnly><ResetPassword /></GuestOnly>} />
              <Route path="/onboarding" element={<GuestOnly><Onboarding /></GuestOnly>} />
              <Route path="/confirm/:token" element={<ConfirmPage />} />
              <Route path="/portal/:token" element={<Portal />} />
              <Route path="/pay/:token" element={<PayInvoice />} />
              <Route path="/approve/:token" element={<ApproveQuote />} />
              <Route path="/change-order/:token" element={<ApproveChangeOrder />} />
              <Route path="/payment/success" element={<Protected><PaymentResult success /></Protected>} />
              <Route path="/payment/cancel" element={<Protected><PaymentResult success={false} /></Protected>} />
              <Route element={<Protected><Layout /></Protected>}>
                <Route path="/my-work" element={<MyWork />} />
                <Route path="/field" element={<Field />} />
                <Route path="/command" element={<CommandCentre />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:id" element={<CustomerDetail />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/purchasing" element={<Purchasing />} />
                <Route path="/receiving" element={<Receiving />} />
                <Route path="/equipment" element={<Equipment />} />
                <Route path="/fleet" element={<Fleet />} />
                <Route path="/properties" element={<Properties />} />
                <Route path="/properties/:id" element={<PropertyDetail />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/vehicles" element={<VehicleLookup />} />
                <Route path="/price-book" element={<PriceBook />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/agreements" element={<Agreements />} />
                <Route path="/safety" element={<Safety />} />
                <Route path="/people" element={<People />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/support" element={<Support />} />
                <Route path="/platform" element={<PlatformAdmin />} />
                <Route path="/platform/orgs/:id" element={<PlatformOrgConfig />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
